// Header-mounted sign-in pill. Shows current state:
//   - "Sign in with Microsoft" (signed out, MSAL configured)
//   - "MS Graph not configured" (env vars missing)
//   - "<Display Name> ▾" (signed in — click to sign out)

import { useEffect, useState } from 'react';
import {
  ensureMsalInitialized,
  getActiveAccount,
  signIn,
  signOut,
} from '@/shared/auth';
import { msalConfigured } from '@/shared/msalConfig';

interface AccountSummary {
  name: string;
  email: string;
}

export function MicrosoftAuthButton() {
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await ensureMsalInitialized();
      if (cancelled || !ok) return;
      const a = getActiveAccount();
      if (a) setAccount({ name: a.name ?? a.username, email: a.username });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSignIn() {
    setBusy(true);
    setError(null);
    try {
      // Redirect flow — page navigates away to Microsoft; code below only runs on error.
      await signIn();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setMenuOpen(false);
    setBusy(true);
    setError(null);
    try {
      await signOut();
      setAccount(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!msalConfigured) {
    return (
      <span
        className="text-xs px-2 py-1 rounded bg-amber/20 border border-amber/30 text-white"
        title="Set VITE_MSAL_CLIENT_ID + VITE_MSAL_TENANT_ID in .env.local to enable Microsoft auth"
      >
        ⚙ MS Graph not configured
      </span>
    );
  }

  if (account) {
    return (
      <div className="relative">
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          disabled={busy}
          className="text-xs px-2 py-1 rounded bg-paper/10 hover:bg-paper/20 border border-paper/20 text-white"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          ✓ {account.name} ▾
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-1 bg-paper text-text border border-line-dark rounded shadow-lg min-w-[180px] text-xs z-50">
            <div className="px-3 py-2 border-b border-line text-text-mute">
              <div className="font-mono">{account.email}</div>
            </div>
            <button
              onClick={handleSignOut}
              disabled={busy}
              className="w-full text-left px-3 py-2 hover:bg-bg text-orange"
            >
              Sign out
            </button>
          </div>
        )}
        {error && (
          <div className="absolute right-0 mt-1 bg-orange text-white px-2 py-1 rounded text-xs">
            ⚠ {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={handleSignIn}
        disabled={busy}
        className="text-xs px-3 py-1 rounded bg-paper text-navy font-semibold hover:opacity-90 disabled:opacity-50"
      >
        {busy ? 'Signing in…' : 'Sign in with Microsoft'}
      </button>
      {error && (
        <div className="absolute right-0 top-full mt-1 bg-orange text-white px-2 py-1 rounded text-xs max-w-xs">
          ⚠ {error}
        </div>
      )}
    </div>
  );
}
